/**
 * Sponsored ad slot — eyebrow + headline + subhead + CTA + optional
 * image, in either a boxed {@code 'card'} or wider {@code 'wide'} variant.
 * Ported from ThemeMaker's {@code blocks/promo/AdBanner}.
 *
 * <p>The {@code 'sponsored'} eyebrow is on by default for ad-disclosure
 * compliance. Use {@code tone='self'} for first-party promotion or
 * {@code 'none'} to suppress it entirely (use sparingly).
 *
 * <p>Deliberately does not track impressions or clicks — wire that via
 * the host's analytics layer if needed.
 */
import { Card } from '@akademi/ui';
import { tx, pickEnum } from '../_shared/coerce';
import { PortalLink } from '@akademi/site-builder-runtime';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface AdBannerProps {
  tone?: 'sponsored' | 'self' | 'none';
  brand?: string;
  headline?: Localized;
  subhead?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  imageSrc?: string;
  imageAlt?: string;
  variant?: 'card' | 'wide';
}

export function AdBanner({
  tone,
  brand,
  headline,
  subhead,
  ctaLabel,
  ctaHref,
  imageSrc,
  imageAlt,
  variant,
}: AdBannerProps) {
  const t = pickEnum<'sponsored' | 'self' | 'none'>(
    tone,
    ['sponsored', 'self', 'none'] as const,
    'sponsored',
  );
  const v = pickEnum<'card' | 'wide'>(variant, ['card', 'wide'] as const, 'card');

  const eyebrow = t === 'sponsored' ? 'Sponsored' : t === 'self' ? `From ${brand ?? 'us'}` : null;

  const h = tx(headline);
  const s = tx(subhead);
  const cl = tx(ctaLabel);

  const cls = `ad-banner ad-banner--${v}`;
  const css = `
    /* Rendered as <Card>: border + elevation come from the component. */
    .ad-banner {
      position: relative; padding: 20px;
      border-radius: var(--radius-lg, 12px);
      background: var(--color-surface-card, #fff);
      display: grid; gap: 16px;
      grid-template-columns: 1fr;
    }
    .ad-banner--wide {
      border-radius: var(--radius-md, 8px);
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
    }
    @media (min-width: 600px) {
      .ad-banner--card {
        grid-template-columns: ${imageSrc ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)'};
        align-items: center;
      }
    }
    .ad-banner__eyebrow {
      font-size: 10px; letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--color-text-secondary, #71717a);
      font-weight: 600;
    }
    .ad-banner__title {
      margin: 0; font-size: 18px; font-weight: 600;
      line-height: 1.3;
      color: var(--color-text-primary, #111);
    }
    .ad-banner__subhead {
      margin: 6px 0 0; font-size: 14px; line-height: 1.5;
      color: var(--color-text-secondary, #71717a);
    }
    .ad-banner__cta {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 10px 18px;
      border-radius: var(--radius-md, 8px);
      background: var(--color-brand-600, #4f46e5);
      color: #fff;
      font-size: 14px; font-weight: 600;
      text-decoration: none;
      transition: background 120ms ease;
      min-height: 44px; box-sizing: border-box;
      white-space: nowrap;
      margin-top: 12px;
    }
    .ad-banner--wide .ad-banner__cta { margin-top: 0; }
    .ad-banner__cta:hover {
      background: var(--color-brand-700, #4338ca);
    }
    .ad-banner__cta:focus-visible {
      outline: 2px solid var(--color-brand-300, #a5b4fc);
      outline-offset: 2px;
    }
    .ad-banner__image {
      width: 100%; max-width: 160px; height: auto;
      aspect-ratio: 1 / 1; object-fit: cover;
      border-radius: var(--radius-sm, 4px);
      display: block;
    }
  `;

  return (
    <BlockRoot
      as="aside"
      style={{ display: 'grid' }}
      aria-label={t === 'sponsored' ? 'Sponsored content' : undefined}
    >
      <Card className={cls}>
        <style dangerouslySetInnerHTML={{ __html: css }} />
        {imageSrc && v === 'card' && (
          <img
            className="ad-banner__image"
            src={imageSrc}
            alt={imageAlt ?? ''}
            loading="lazy"
            decoding="async"
          />
        )}
        <div>
          {eyebrow && <div className="ad-banner__eyebrow">{eyebrow}</div>}
          {h && <h3 className="ad-banner__title">{h}</h3>}
          {s && <p className="ad-banner__subhead">{s}</p>}
          {cl && ctaHref && (
            <PortalLink href={ctaHref} className="ad-banner__cta">
              {cl}
            </PortalLink>
          )}
        </div>
      </Card>
    </BlockRoot>
  );
}
